
/**
 * Google Sheet Integration Service
 * Sends order data to a Google Apps Script Web App
 */

import { getDatabase, ref, query, orderByChild, equalTo, get } from 'firebase/database';

// TODO: Replace with your deployed Web App URL
const GOOGLE_SCRIPT_URL = "https://script.google.com/macros/s/AKfycbxISbafAFsK8Xey6SoZOOm_kMUP8BFZWRFsPod4xMllm8qRyyknHyoRv4hoVwcVBm1g/exec";

export const GoogleSheetService = {
    syncOrder: async (order) => {
        if (!GOOGLE_SCRIPT_URL) {
            console.warn("[GoogleSheets] No Script URL configured. Skipping sync.");
            return { success: false, error: "Configuration Missing" };
        }

        try {
            // Check for Repeated Customer (Query Firebase)
            const db = getDatabase();
            const mobile = order.customer?.mobile;
            let isRepeated = false;

            if (mobile) {
                // We assume data structure: orders/{id}/customer/mobile
                // Note: Querying by deep path requires index on 'customer/mobile' in .json rules
                // Fallback: If no index, fetch limitToLast(500) and client-filter (less efficient but works without rules update)
                // Let's rely on orderByChild('customer/mobile') assuming rule exists or small dataset.
                // Actually, let's use a broader check or just simple logic if we have access to context.
                // Safest without rule change: fetch orders and filter. BUT that is heavy.
                // Better: Assume user has many orders. 
                // Let's try the query. If it fails due to index, we catch it.
                try {
                    const q = query(ref(db, 'orders'), orderByChild('customer/mobile'), equalTo(String(mobile)));
                    const snapshot = await get(q);
                    if (snapshot.exists()) {
                        // If more than 1 order exists for this number (including current one if saved)
                        // Or if current is new (not saved yet?), then > 0 means repeated.
                        // Ideally checking if count > 1 (current + at least one past)
                        // But since we might sync *after* save, 
                        // Let's assume if size > 1 => Repeat. 
                        // If size === 1 => New (only this one).
                        // However, if we sync *before* save or concurrently, it's tricky.
                        // Let's assume > 1 just to be safe for "Repeat".
                        isRepeated = Object.keys(snapshot.val()).length > 1;
                    }
                } catch (err) {
                    console.warn("Could not check repeat customer status:", err);
                }
            }

            // Calculate Sheet Name (DD-MM-YYYY)
            const dateObj = new Date(order.createdAt || new Date());
            const day = String(dateObj.getDate()).padStart(2, '0');
            const month = String(dateObj.getMonth() + 1).padStart(2, '0');
            const year = dateObj.getFullYear();

            // Determine PPD or COD suffix
            const mode = (order.payment?.mode === 'Prepaid') ? 'PPD' : 'COD';
            const sheetName = `${day}-${month}-${year} ${mode}`;

            // Prepare Payload: Ensure 'courier' reflects preference if not yet shipped
            const finalCourier = order.shipping?.courierPreference || order.shipping?.courier || 'Normal';

            // We construct a modified shipping object for the payload to satisfy the Script's expectation
            // Script expects: data.shipping.courier
            const shippingPayload = {
                ...(order.shipping || {}),
                courier: finalCourier
            };

            const payload = {
                ...order,
                shipping: shippingPayload,
                targetSheetName: sheetName,
                isRepeatedCustomer: isRepeated
            };

            await fetch(GOOGLE_SCRIPT_URL, {
                method: "POST",
                mode: "no-cors",
                headers: {
                    "Content-Type": "text/plain",
                },
                body: JSON.stringify(payload)
            });

            console.log("[GoogleSheets] Order synced successfully");
            return { success: true };
        } catch (error) {
            console.error("[GoogleSheets] Sync Failed:", error);
            return { success: false, error: error.message };
        }
    },

    syncShipmentLog: async (order, shipmentData) => {
        if (!GOOGLE_SCRIPT_URL) return;
        try {
            const payload = {
                sheetType: 'SHIPMENT_LOG',
                orderId: order.id || order.firebaseKey,
                orderDate: order.date || new Date().toISOString(),
                customerName: order.customer?.name,
                customerMobile: order.customer?.mobile,
                courier: shipmentData.courierName || 'Shiprocket',
                awb: shipmentData.awb,
                items: order.items,
                status: 'Shipped'
            };

            await fetch(GOOGLE_SCRIPT_URL, {
                method: "POST",
                mode: "no-cors",
                headers: { "Content-Type": "text/plain" },
                body: JSON.stringify(payload)
            });
            console.log("[GoogleSheets] Shipment Logged");
        } catch (e) {
            console.error("[GoogleSheets] Log Failed:", e);
        }
    }
};
